import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Slide } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { TOP_BAR_HEIGHT } from './navConfig';

interface BannerNotice {
  title: string;
  message: string;
  path?: string;
}

/**
 * Shows a portal notification across the top of the admin app.
 * Only events marked as a banner are shown. Nothing is emailed.
 */
export function PortalNotificationBanner() {
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<BannerNotice | null>(null);

  useEffect(() => {
    if (!socket) return;
    const onNew = (payload: { title?: string; message?: string; path?: string; banner?: boolean }) => {
      if (!payload?.banner || !payload.title) return;
      setNotice({
        title: payload.title,
        message: payload.message || '',
        path: payload.path,
      });
    };
    socket.on('admin:notification:new', onNew);
    return () => {
      socket.off('admin:notification:new', onNew);
    };
  }, [socket]);

  return (
    <Slide direction="down" in={!!notice} mountOnEnter unmountOnExit>
      <Box sx={{ position: 'absolute', top: TOP_BAR_HEIGHT, left: 0, right: 0, zIndex: 1190 }}>
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setNotice(null)}
        action={
          notice?.path ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const path = notice.path;
                setNotice(null);
                if (path) navigate(path);
              }}
            >
              Open
            </Button>
          ) : undefined
        }
        sx={{
          borderRadius: 0,
          alignItems: 'center',
          '& .MuiAlert-message': { py: 0.5 },
        }}
      >
        <AlertTitle sx={{ mb: 0 }}>{notice?.title}</AlertTitle>
        {notice?.message}
      </Alert>
      </Box>
    </Slide>
  );
}
